import { ChevronLeft, ChevronRight, ExpandLess, ExpandMore } from '@mui/icons-material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupIcon from '@mui/icons-material/Group';
import MessageIcon from '@mui/icons-material/Message';
import QueueIcon from '@mui/icons-material/Queue';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import { Box, Button, Typography } from '@mui/material';
import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CHATYME_EMBED_HOST_MESSAGE_TYPE } from '../constants/chatymeEmbedHostMessageTypes';
import { useAuthContext } from '../contexts/AuthContext';
import { ChatymeEmbedHelper, UserHelper } from '../helpers';
import '../styles/screens/backoffice.css';
import '../styles/screens/common.css';
import { BackofficeMenu } from '../types/backoffice';
import ChatymeLoading from './common/ChatymeLoading';

interface MenuChild {
  id: string;
  label: string;
  path: string;
}

interface MenuItem {
  key: BackofficeMenu;
  icon: string;
  path: string;
  children?: MenuChild[];
}

const AI_HUB_PATH = '/backoffice-ai-hub';

const menuItems: MenuItem[] = [
  { key: BackofficeMenu.Dashboard, icon: 'space_dashboard', path: '/backoffice' },
  { key: BackofficeMenu.Users, icon: 'group', path: '/backoffice-users' },
  { key: BackofficeMenu.Messages, icon: 'message', path: '/backoffice-messages' },
  {
    key: BackofficeMenu.AiHub,
    icon: 'auto_awesome',
    path: AI_HUB_PATH,
    children: [
      { id: 'dashboard', label: 'Dashboard', path: AI_HUB_PATH },
      { id: 'prompts', label: 'Prompts', path: `${AI_HUB_PATH}?tab=prompts` },
      { id: 'tools', label: 'Tools', path: `${AI_HUB_PATH}?tab=tools` },
      { id: 'contact', label: 'Contact', path: `${AI_HUB_PATH}?tab=contact` },
      { id: 'moderation', label: 'Moderation', path: `${AI_HUB_PATH}?tab=moderation` },
      { id: 'prompt-eval', label: 'Prompt Eval', path: `${AI_HUB_PATH}?tab=prompt-eval` },
      { id: 'settings', label: 'Settings', path: `${AI_HUB_PATH}?tab=settings` }
    ]
  },
  { key: BackofficeMenu.BusinessHours, icon: 'schedule', path: '/backoffice-business-hours' },
  { key: BackofficeMenu.Reports, icon: 'assessment', path: '/backoffice-reports' },
  { key: BackofficeMenu.Scheduler, icon: 'calendar_month', path: '/backoffice-scheduler' },
  { key: BackofficeMenu.QueueMonitor, icon: 'queue', path: '/backoffice-queue-monitor' }
];

const getMenuIcon = (iconName: string) => {
  switch (iconName) {
    case 'space_dashboard':
      return <SpaceDashboardIcon />;
    case 'group':
      return <GroupIcon />;
    case 'message':
      return <MessageIcon />;
    case 'auto_awesome':
      return <AutoAwesomeIcon />;
    case 'schedule':
      return <ScheduleIcon />;
    case 'assessment':
      return <AssessmentIcon />;
    case 'calendar_month':
      return <CalendarMonthIcon />;
    case 'queue':
      return <QueueIcon />;
    default:
      return null;
  }
};

const getChildTabParam = (childPath: string): string | null => {
  const queryIndex = childPath.indexOf('?');
  if (queryIndex < 0) {
    return null;
  }
  return new URLSearchParams(childPath.slice(queryIndex + 1)).get('tab');
};

const isMenuChildActive = (child: MenuChild, pathname: string, search: string): boolean => {
  const pathOnly = child.path.split('?')[0];
  if (pathname !== pathOnly) {
    return false;
  }
  const childTab = getChildTabParam(child.path);
  const currentTab = new URLSearchParams(search).get('tab');
  if (!childTab) {
    return !currentTab || currentTab === 'dashboard';
  }
  return currentTab === childTab;
};

type IndicatorState = {
  top: number;
  height: number;
  visible: boolean;
};

const BackofficeShell: React.FC = () => {
  const { userRole, userName, userSurname, userEmail, logout } = useAuthContext();
  const [isMenuExpanded, setIsMenuExpanded] = useState(true);
  const [aiHubSubmenuOpen, setAiHubSubmenuOpen] = useState(false);
  const [indicator, setIndicator] = useState<IndicatorState>({
    top: 0,
    height: 0,
    visible: false
  });
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef<HTMLElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const filteredMenuItems = useMemo(() => {
    const accessibleMenus = UserHelper.getAccessibleBackofficeMenus(userRole);
    return menuItems.filter(item => accessibleMenus.includes(item.key));
  }, [userRole]);

  const activeMenu = useMemo(() => {
    const match = filteredMenuItems.find(item =>
      item.path === '/backoffice'
        ? location.pathname === item.path
        : location.pathname.startsWith(item.path)
    );
    return match?.key ?? BackofficeMenu.Dashboard;
  }, [location.pathname, filteredMenuItems]);

  const isOnAiHub = location.pathname.startsWith(AI_HUB_PATH);

  const activeIndicatorKey = useMemo(() => {
    const aiHub = filteredMenuItems.find(item => item.key === BackofficeMenu.AiHub);
    if (aiHub?.children && isOnAiHub) {
      const activeChild = aiHub.children.find(child =>
        isMenuChildActive(child, location.pathname, location.search)
      );
      if (activeChild) {
        return `child:${activeChild.id}`;
      }
    }
    if (activeMenu === BackofficeMenu.AiHub) {
      return `parent:${BackofficeMenu.AiHub}`;
    }
    return `parent:${activeMenu}`;
  }, [activeMenu, filteredMenuItems, isOnAiHub, location.pathname, location.search]);

  const moveIndicatorToKey = useCallback((key: string | null) => {
    const navEl = navRef.current;
    if (!navEl || !key) {
      setIndicator(prev => ({ ...prev, visible: false }));
      return;
    }
    const target = itemRefs.current[key];
    if (!target) {
      setIndicator(prev => ({ ...prev, visible: false }));
      return;
    }
    const navRect = navEl.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    setIndicator({
      top: targetRect.top - navRect.top + navEl.scrollTop,
      height: targetRect.height,
      visible: true
    });
  }, []);

  useEffect(() => {
    if (isOnAiHub) {
      setAiHubSubmenuOpen(true);
    }
  }, [isOnAiHub]);

  useLayoutEffect(() => {
    const paint = () => moveIndicatorToKey(activeIndicatorKey);
    paint();
    const frame = window.requestAnimationFrame(paint);
    return () => window.cancelAnimationFrame(frame);
  }, [
    activeIndicatorKey,
    aiHubSubmenuOpen,
    isMenuExpanded,
    moveIndicatorToKey,
    filteredMenuItems
  ]);

  useEffect(() => {
    const onResize = () => {
      moveIndicatorToKey(activeIndicatorKey);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [activeIndicatorKey, moveIndicatorToKey]);

  useEffect(() => {
    const clientAppUrl = process.env.REACT_APP_CLIENT_APP_URL;
    const chatAppUrl = process.env.REACT_APP_CHAT_APP_URL;

    if (!clientAppUrl || !chatAppUrl) {
      console.error('[chatyme] REACT_APP_CLIENT_APP_URL and REACT_APP_CHAT_APP_URL are required');
      return;
    }

    const embed = new ChatymeEmbedHelper();
    embed.load(
      {
        client_app_url: clientAppUrl,
        chat_app_url: chatAppUrl,
        chat_overlay: 'backoffice',
        config: {
          screen_type: 'agent',
          bot_id: 'cp4BZkcSIHn1Vdq8rNoyH0GFLNLyDsDs',
          language: 'es',
          locale: 'es-ES',
          user: {
            name: userName,
            surname: userSurname,
            email: userEmail,
            role: userRole
          }
        }
      },
      event => {
        if (event.data?.type === CHATYME_EMBED_HOST_MESSAGE_TYPE.CHAT_TOKEN_EXPIRED) {
          logout();
        }
      }
    );

    return () => embed.destroy();
  }, [logout, userEmail, userName, userSurname, userRole]);

  const handleMenuClick = (menu: MenuItem) => {
    if (menu.children && menu.children.length > 0) {
      if (!isMenuExpanded) {
        setIsMenuExpanded(true);
        setAiHubSubmenuOpen(true);
        navigate(menu.path);
        return;
      }
      if (location.pathname.startsWith(menu.path)) {
        setAiHubSubmenuOpen(prev => !prev);
      } else {
        setAiHubSubmenuOpen(true);
        navigate(menu.path);
      }
      return;
    }
    navigate(menu.path);
  };

  const setItemRef = (key: string) => (el: HTMLButtonElement | null) => {
    itemRefs.current[key] = el;
  };

  return (
    <Box
      className="chatyme-backoffice-shell"
      sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.100' }}
    >
      <Box
        component="aside"
        className={`chatyme-backoffice-sidebar ${isMenuExpanded ? 'chatyme-expanded' : 'chatyme-collapsed'}`}
        sx={{ bgcolor: 'grey.900' }}
      >
        <Button
          onClick={() => setIsMenuExpanded(prev => !prev)}
          startIcon={isMenuExpanded ? <ChevronLeft /> : <ChevronRight />}
          sx={{
            color: 'white',
            m: 1,
            lineHeight: 'normal'
          }}
        >
          {isMenuExpanded ? 'Chatyme' : null}
        </Button>

        <Box sx={{ borderBottom: '2px solid white', m: 1 }} />

        <Box
          component="nav"
          ref={navRef}
          className="chatyme-backoffice-nav"
          sx={{ display: 'flex', flexDirection: 'column', px: 1 }}
        >
          <Box
            className={`chatyme-backoffice-nav-indicator ${indicator.visible ? 'chatyme-visible' : ''}`}
            style={{
              transform: `translateY(${indicator.top}px)`,
              height: indicator.height
            }}
            aria-hidden
          />

          {filteredMenuItems.map(menu => {
            const hasChildren = Boolean(menu.children?.length);
            const isParentActive = activeMenu === menu.key;
            const showChildren =
              hasChildren && aiHubSubmenuOpen && isMenuExpanded && menu.key === BackofficeMenu.AiHub;
            const isLeafSelected = isParentActive && !hasChildren;
            const parentKey = `parent:${menu.key}`;

            return (
              <Box key={menu.key} className="chatyme-backoffice-nav-group">
                <button
                  type="button"
                  ref={setItemRef(parentKey)}
                  className={[
                    'chatyme-backoffice-nav-item',
                    isLeafSelected ? 'chatyme-selected' : '',
                    isParentActive && hasChildren ? 'chatyme-section-active' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleMenuClick(menu)}
                  aria-expanded={hasChildren ? showChildren : undefined}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                    {getMenuIcon(menu.icon)}
                    {isMenuExpanded && (
                      <Typography component="span" sx={{ fontWeight: 600 }}>
                        {menu.key}
                      </Typography>
                    )}
                  </Box>
                  {hasChildren && isMenuExpanded && (
                    <Box component="span" className="chatyme-backoffice-nav-chevron" aria-hidden>
                      {showChildren ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                  )}
                </button>

                {showChildren && menu.children && (
                  <Box
                    className="chatyme-backoffice-nav-children"
                    role="group"
                    aria-label={`${menu.key} sections`}
                  >
                    {menu.children.map(child => {
                      const childKey = `child:${child.id}`;
                      const childActive = isMenuChildActive(
                        child,
                        location.pathname,
                        location.search
                      );
                      return (
                        <button
                          key={child.id}
                          type="button"
                          ref={setItemRef(childKey)}
                          className={`chatyme-backoffice-nav-child ${childActive ? 'chatyme-selected' : ''}`}
                          onClick={() => navigate(child.path)}
                        >
                          {child.label}
                        </button>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        className="chatyme-backoffice-content"
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}
      >
        <Suspense fallback={<ChatymeLoading variant="content" />}>
          <Outlet />
        </Suspense>
      </Box>
    </Box>
  );
};

export default BackofficeShell;
